import { ArrowRight, Upload } from 'lucide-react';

import { ENGAGEMENT_MODULES } from '@/components/engagement/module-cards';
import type { ModulesStepState, NewEngagementWizardState } from '@/lib/wizard/new-engagement-state';
import { cn } from '@/lib/utils/cn';

/**
 * Step 3 — Next steps pane; the wizard's last step when the engagement has
 * no IV&V module (Discovery and/or Selection only; `usesContractBaseline`
 * in the wizard state module says which modules count).
 *
 * Such an engagement has no SOW to read and no contract baseline to form:
 * the API refuses a base-formation run for it and the worker's auto path
 * skips its documents. So instead of the Baseline documents and AI base
 * formation panes, this pane says where to start for the modules chosen
 * (E12-F02 for Discovery: choose the framework, record client facts, name
 * the people to interview; the Selection requirements screen; and the
 * Documents screen, where uploads go for every engagement). Each row closes
 * the wizard and opens that screen; "Open engagement" in the footer opens
 * the overview as on the contract flow.
 *
 * Visual contract: the wizard's own pane pieces — the note card from the
 * Modules step and rows in the phase-card look of the base formation step
 * (`rounded-card border-ink-100`, 22px pips swapped for the module cards'
 * icon tile). Tokens only; no new colours.
 */
export type NextStepRoute =
  | '/engagements/$engagementId/discovery/setup'
  | '/engagements/$engagementId/selection'
  | '/engagements/$engagementId/documents';

export interface NextStepRow {
  key: 'discovery' | 'selection' | 'documents';
  title: string;
  sub: string;
  to: NextStepRoute;
  Icon: typeof Upload;
}

const moduleIcon = (key: 'discovery' | 'selection') =>
  ENGAGEMENT_MODULES.find((m) => m.key === key)?.Icon ?? Upload;

/**
 * The rows for these modules, in the order to do them: Discovery set up
 * first (it names the framework everything else hangs off), then the
 * Selection requirements, then Documents, which every engagement has.
 */
export function nextStepsFor(modules: ModulesStepState): NextStepRow[] {
  const rows: NextStepRow[] = [];
  if (modules.discovery) {
    rows.push({
      key: 'discovery',
      title: 'Set up discovery',
      sub: 'Choose the framework, record the client facts and name the people to interview.',
      to: '/engagements/$engagementId/discovery/setup',
      Icon: moduleIcon('discovery'),
    });
  }
  if (modules.selection || modules.discovery) {
    rows.push({
      key: 'selection',
      title: 'Add requirements',
      sub: 'Pick requirements from the firm library or write them for this client, each with a criticality and a citation.',
      to: '/engagements/$engagementId/selection',
      Icon: moduleIcon('selection'),
    });
  }
  rows.push({
    key: 'documents',
    title: 'Upload documents',
    sub: 'Client documents, questionnaires and interview notes go through the Documents screen.',
    to: '/engagements/$engagementId/documents',
    Icon: Upload,
  });
  return rows;
}

export interface StepProps {
  state: NewEngagementWizardState;
  /** Closes the wizard and opens the screen. */
  onGo: (to: NextStepRoute) => void;
}

export function StepNextSteps({ state, onGo }: StepProps) {
  if (!state.engagement) {
    return (
      <div className="rounded-card border border-cite-broken/30 bg-paper-50 p-3 text-meta text-cite-broken">
        Engagement not yet created. Go back to step 2 (Modules) and Continue to create it.
      </div>
    );
  }
  const rows = nextStepsFor(state.modules);

  return (
    <div className="space-y-4">
      <div role="status" className="rounded-card border border-ink-100 bg-paper-50 p-3.5">
        <p className="text-meta leading-[18px] text-ink-500">
          The engagement is created. It has no IV&amp;V module, so there is no SOW to read and no AI
          base formation run to start: Caliber forms no contract baseline for it. Start with the
          steps below, in order.
        </p>
      </div>

      <ol className="grid gap-3">
        {rows.map((row) => (
          <li key={row.key}>
            <button
              type="button"
              onClick={() => onGo(row.to)}
              data-next-step={row.key}
              className={cn(
                'flex w-full items-center gap-3 rounded-card border border-ink-100 bg-paper-0 px-4 py-3.5 text-left',
                'hover:border-ink-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              )}
            >
              <span
                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-card bg-paper-100 text-ink-700"
                aria-hidden="true"
              >
                <row.Icon className="h-4.5 w-4.5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-body-sm font-medium text-ink-900">{row.title}</span>
                <span className="mt-0.5 block text-meta text-ink-500">{row.sub}</span>
              </span>
              <ArrowRight className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
